import { ChangeDetectionStrategy, Component, Signal, computed, inject, input, OnInit, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, of, startWith, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { isDeepEqual } from '@fcom/core/utils/utils';
import { DateRange } from '@fcom/ui-components/components/datepicker/interfaces';
import { GlobalBookingTripDates } from '@fcom/common/interfaces/global-booking-trip-dates.interface';
import { SearchType } from '@fcom/common/interfaces/gtm.interface';
import { PriceCalendarCTAParams, PriceCalendarParams } from '@fcom/common/interfaces/price-calendar.interface';
import {
  countFlightsPerDay,
  getDateIdsInRange,
  PriceCalendarService,
  ScheduleBoundsState,
  TimetableSearchService,
} from '@fcom/common';
import * as GlobalBookingActions from '@fcom/common/store/actions/global-booking.actions';
import {
  selectGlobalBookingPaxAmount,
  selectGlobalBookingTravelClass,
  selectGlobalBookingTravelDates,
  selectGlobalBookingTripType,
} from '@fcom/common/store/selectors/global-booking.selectors';
import { transformLocationCode } from '@fcom/ond-marketing-landing-core/services/utils';

interface VisibleFlightsQuery {
  origin: string;
  destination: string;
  start: LocalDate;
  numberOfDays: number;
}

@Component({
  selector: 'cms-calendar-schedule',
  templateUrl: 'cms-calendar-schedule.component.html',
  styleUrls: ['cms-calendar-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsCalendarScheduleComponent implements OnInit {
  readonly pricesAvailable = input<boolean | null>(null);
  readonly initialTripType = input<TripType | null>(null);

  private route = inject(ActivatedRoute);
  private priceCalendarService = inject(PriceCalendarService);
  private store = inject(Store);
  private timetableSearchService = inject(TimetableSearchService);

  readonly SvgLibraryIcon = SvgLibraryIcon;

  private readonly routeParams: Signal<Params> = toSignal(this.route.params, { initialValue: {} as Params });

  readonly origin = computed<string>(() => transformLocationCode(this.routeParams()['originLocationCode']));
  readonly destination = computed<string>(() => transformLocationCode(this.routeParams()['destinationLocationCode']));

  private readonly tripType = this.store.selectSignal(selectGlobalBookingTripType);
  private readonly travelClass = this.store.selectSignal(selectGlobalBookingTravelClass);
  private readonly paxAmount = this.store.selectSignal(selectGlobalBookingPaxAmount);
  private readonly initialTravelDates = this.store.selectSignal(selectGlobalBookingTravelDates);

  readonly openWithParams = computed<PriceCalendarParams | null>(() => {
    const origin = this.origin();
    const destination = this.destination();
    if (!origin || !destination) {
      return null;
    }
    return {
      origin,
      destination,
      tripType: this.tripType(),
      travelClass: this.travelClass(),
      paxAmount: this.paxAmount(),
      initialTravelDates: this.initialTravelDates(),
    };
  });
  readonly openWithParams$ = toObservable(this.openWithParams);

  private readonly departureBoundsState: Signal<ScheduleBoundsState> = toSignal(
    this.priceCalendarService.scheduleBounds$,
    { initialValue: { loading: false, bounds: [] } }
  );
  readonly departureBounds = computed(() => this.departureBoundsState().bounds);
  readonly departureLoading = computed(() => this.departureBoundsState().loading);

  private readonly returnBoundsState: Signal<ScheduleBoundsState> = toSignal(
    this.priceCalendarService.returnScheduleBounds$,
    { initialValue: { loading: false, bounds: [] } }
  );
  readonly returnBounds = computed(() => this.returnBoundsState().bounds);
  readonly returnLoading = computed(() => this.returnBoundsState().loading);

  readonly travelDates = toSignal(this.priceCalendarService.travelDates$);
  readonly hasSelectedDate = computed(() => !!this.travelDates()?.departureDate);
  readonly hasReturnDate = computed(() => !!this.travelDates()?.returnDate);

  private readonly liveTripType = toSignal(this.priceCalendarService.tripType$, { initialValue: TripType.ONEWAY });

  private readonly visibleRange = signal<DateRange | null>(null);

  private readonly livePricesAvailable = toSignal(this.priceCalendarService.pricesAvailable$);

  private readonly hasPrices = computed<boolean>(() => {
    return this.livePricesAvailable() ?? this.pricesAvailable() !== false;
  });

  readonly showReturnTab = computed(
    () => this.tripType() === TripType.RETURN || this.liveTripType() === TripType.RETURN
  );

  private readonly selectingReturn = computed<boolean>(() => {
    return this.showReturnTab() && this.hasSelectedDate() && !this.hasReturnDate();
  });

  private readonly bothDatesSelected = computed<boolean>(() => {
    return this.showReturnTab() && this.hasSelectedDate() && this.hasReturnDate();
  });

  private readonly visibleFlightsQuery = computed<VisibleFlightsQuery | null>(() => {
    const origin = this.origin();
    const destination = this.destination();
    const visibleRange = this.visibleRange();
    if (!origin || !destination || !visibleRange || this.hasPrices() || this.bothDatesSelected()) {
      return null;
    }
    const selectingReturn = this.selectingReturn();
    const departureDate = this.travelDates()?.departureDate;
    const today = LocalDate.now();
    const [rangeStart, end] = visibleRange;
    const earliest = selectingReturn && today.lt(departureDate) ? departureDate : today;
    const start = rangeStart.lt(earliest) ? earliest : rangeStart;
    return {
      origin: selectingReturn ? destination : origin,
      destination: selectingReturn ? origin : destination,
      start,
      numberOfDays: getDateIdsInRange(start, end).length,
    };
  });

  private readonly visibleFlights = toSignal(
    toObservable(this.visibleFlightsQuery).pipe(
      distinctUntilChanged(isDeepEqual),
      switchMap((query) => {
        if (!query) {
          return of<Record<string, FinnairBoundItem[]>>({});
        }
        return this.timetableSearchService
          .getFlights(query.origin, query.destination, query.start, query.numberOfDays)
          .pipe(
            catchError(() => {
              return of<Record<string, FinnairBoundItem[]>>({});
            }),
            startWith<Record<string, FinnairBoundItem[]>>({})
          );
      })
    ),
    { initialValue: {} as Record<string, FinnairBoundItem[]> }
  );

  private readonly selectedDateCounts = computed<Record<string, number>>(() => {
    const { departureDate, returnDate } = this.travelDates();
    const counts: Record<string, number> = {};
    if (!this.returnLoading()) {
      counts[returnDate.id] = this.returnBounds().length;
    }
    if (!this.departureLoading()) {
      counts[departureDate.id] = this.departureBounds().length;
    }
    return counts;
  });

  readonly flightCounts = computed<Record<string, number>>(() => {
    if (this.hasPrices()) {
      return {};
    }
    if (this.bothDatesSelected()) {
      return this.selectedDateCounts();
    }
    return countFlightsPerDay(this.visibleFlights());
  });

  onVisibleRangeChange(range: DateRange): void {
    this.visibleRange.set(range);
  }

  onBookFlights(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params, SearchType.PROMOTION);
  }

  onTravelDatesChanged(dates: GlobalBookingTripDates): void {
    this.store.dispatch(GlobalBookingActions.setFlightDate({ dates, index: 0 }));
  }

  ngOnInit(): void {
    const initialTripType = this.initialTripType();
    if (initialTripType && initialTripType !== this.tripType()) {
      this.store.dispatch(GlobalBookingActions.setTripType({ tripType: initialTripType }));
    }
  }

  onAddReturn(): void {
    this.store.dispatch(GlobalBookingActions.setTripType({ tripType: TripType.RETURN }));
  }
}
